import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import { format, parseISO } from 'date-fns';

// Component to fetch and store date and time formats in sessionStorage
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);

  return null;
};

const RejectionTab = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [disapprovedCases, setDisapprovedCases] = useState([]);
  const [filteredDisapprovedCases, setFilteredDisapprovedCases] = useState([]);
  const [page, setPage] = useState(1);
  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [medicalCenterId, setMedicalCenterId] = useState(null);
  const [medicalCenterName, setMedicalCenterName] = useState(null);
  const [filterForm, setFilterForm] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const limit = 10;

  const location = useLocation();
  const navigate = useNavigate();
  const { settings, settingsRef, updateSetting, handleColorTheme } = useSettings();

  // Get date and time format from sessionStorage
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Utility function to format date based on settings
  const formatCaseDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
    const timeFormat = sessionStorage.getItem('time_format') || '24';

    // Map WordPress-style formats to date-fns syntax
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    // ✅ Parse both ISO and non-ISO timestamps as *UTC*, not local time
    let utcDate;
    if (dateString.includes('T')) {
      // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
      utcDate = new Date(dateString);
    } else {
      // MySQL-like (e.g. 2025-10-27 16:35:40)
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
    }

    // Format without applying local timezone
    const utcTimestamp = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds()
    );

    return format(utcTimestamp, formatString);
  } catch (err) {
    console.error('Error formatting date:', err, dateString);
    return 'Invalid Date';
  }
};

  

  // Form data for add/edit modal
  const [formData, setFormData] = useState({
    user_id: userId,
    case_name: '',
    case_location: '',
    case_area: '',
    nearest_medical_center: '',
    injury_type: '',
    age: '',
    gender: '',
    triage_card: '',
    bracelet_id: '',
    contact_number: '',
    documents: [],
    ems_member: '',
    ems_member_name: '',
    nearest_ambulance: '',
    location: null,
    area: null,
    medical_center: null,
  });

  // Load role, user, and medical center info from sessionStorage
  useEffect(() => {
    const storedRoleId = sessionStorage.getItem('Role_id');
    const storedRoleName = sessionStorage.getItem('Role_Name');
    const storedUserId = sessionStorage.getItem('user_id');
    const storedMedicalCenterId = sessionStorage.getItem('medical_center_id');
    const storedMedicalCenterName = sessionStorage.getItem('Medical_Center_Name');

    if (!storedRoleId || !storedUserId) {
      setError('Missing role or user information. Please log in again.');
      navigate('/login');
      return;
    }

    setRoleId(storedRoleId);
    setRoleName(storedRoleName);
    setUserId(storedUserId);
    setMedicalCenterId(storedMedicalCenterId);
    setMedicalCenterName(storedMedicalCenterName || 'N/A');
  }, [location, navigate]);

  // Fetch disapproved cases once role & user are available
  useEffect(() => {
    if (roleId && userId) {
      fetchDisapprovedCases();
    }
  }, [roleId, userId]);

  // Apply filtering
  useEffect(() => {
    const filterCases = (cases) =>
      Array.isArray(cases)
        ? cases.filter((c) =>
            filterForm.name
              ? c.case_name?.toLowerCase().includes(filterForm.name.toLowerCase())
              : true
          )
        : [];

    setFilteredDisapprovedCases(filterCases(disapprovedCases));
  }, [disapprovedCases, filterForm]);

  // Fetch disapproved cases from API
  // Fetch disapproved cases from API
const fetchDisapprovedCases = async () => {
  try {
    setLoading(true);
    setError(null);

    const token = sessionStorage.getItem('auth_token');
    if (!token) {
      setError('No authentication token found. Please log in.');
      navigate('/login');
      return;
    }

    const response = await axiosInstance.get(
      `/ems/records/${roleId}/ems_disapproved_cases/${userId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    let data = [];

    // Handle API response
    if (Array.isArray(response.data)) {
      data = response.data;
    } else if (response.data?.data) {
      data = response.data.data;
    }

    // Save into state
    setDisapprovedCases(data);

  } catch (error) {
    console.error('Error fetching disapproved cases:', error);

    if (error.response?.status === 401) {
      setError('Unauthorized. Please log in again.');
      navigate('/login');
    } else if (error.response?.status === 404) {
      setError('No Rejected cases found.');
      setDisapprovedCases([]); // empty list
    } else {
      setError(error.response?.data?.message || 'Failed to fetch disapproved cases.');
    }
  } finally {
    setLoading(false);
  }
};


  // Filter controls
  const handleFilterChange = (e) => {
    setFilterForm({ ...filterForm, [e.target.name]: e.target.value });
    setPage(1);
  };

  const clearFilters = () => {
    setFilterForm({ name: '' });
    setPage(1);
  };

  // Navigate to case details
  const handleCaseDetails = (caseItem) => {
    sessionStorage.setItem('currentCaseId', caseItem.id);
    sessionStorage.setItem('RejectionTabCaseStatus', caseItem.case_status);
    sessionStorage.setItem('ClosedTabCaseStatus', caseItem.casestatus);
    navigate('/ems/rejects-case-details');
  };

  // Open modal for adding a new case
  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentCaseId(null);
    setFormData({
      user_id: userId,
      case_name: '',
      case_location: '',
      case_area: '',
      nearest_medical_center: '',
      injury_type: '',
      age: '',
      gender: '',
      triage_card: '',
      bracelet_id: '',
      contact_number: '',
      documents: [],
      ems_member: '',
      ems_member_name: '',
      nearest_ambulance: '',
      location: null,
      area: null,
      medical_center: null,
    });
    setShowModal(true);
  };

  // Handle editing an existing case
  const handleEdit = async (caseItem) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/ems/cases/${caseItem.id}`);
      const caseData = response.data.data || response.data;

      setIsEditMode(true);
      setCurrentCaseId(caseItem.id);
      setFormData({
        user_id: userId,
        case_name: caseData.case_name || '',
        case_location: caseData.case_location || '',
        case_area: caseData.case_area || '',
        nearest_medical_center: caseData.nearest_medical_center || '',
        injury_type: caseData.injury_type || '',
        age: caseData.age || '',
        gender: caseData.gender || '',
        triage_card: caseData.triage_card || '',
        bracelet_id: caseData.bracelet_id || '',
        contact_number: caseData.contact_number || '',
        documents: caseData.documents || [],
        ems_member: caseData.ems_member || '',
        ems_member_name: caseData.ems_member_name || '',
        nearest_ambulance: caseData.ambulance?.plate_number || '',
        case_status: caseData.case_status || '1',
        medical_center: caseData.medical_center || null,
        from_medical_center: caseData.from_medical_center || null,
        location: caseData.location || null,
        area: caseData.area || null,
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching case details:', error);
      showAlert('Failed to load case details', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle saving case data (add or edit)
  const handleSave = async (formDataToSend) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('auth_token');

      const url = isEditMode ? `/ems/cases/${currentCaseId}` : '/ems/cases';
      await axiosInstance.post(url, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      showAlert(`Case ${isEditMode ? 'updated' : 'added'} successfully!`);
      setShowModal(false);
      fetchDisapprovedCases();
    } catch (error) {
      console.error('Save failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save case';
      showAlert(errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  // Show alert messages
  const showAlert = (message, isSuccess = true) =>
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);

  // Table rendering
  const renderTable = () => {
    const currentItems = filteredDisapprovedCases.slice((page - 1) * limit, page * limit);

    return (
      <>
        <div className="table-responsive rounded-2 ros-table">
          <table className="table text-nowrap customize-table mb-0 align-middle">
            <thead className="text-dark fs-4">
              <tr>
                <th><h6 className="fw-semibold mb-0">ID</h6></th>
                <th><h6 className="fw-semibold mb-0">Name</h6></th>
                <th><h6 className="fw-semibold mb-0">Transfer To</h6></th>
                <th><h6 className="fw-semibold mb-0">Disapproval Notes</h6></th>
                <th><h6 className="fw-semibold mb-0">Rejected At</h6></th>
                <th><h6 className="fw-semibold mb-0">Action</h6></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredDisapprovedCases.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">No Rejected cases found</td>
                </tr>
              ) : (
                currentItems.map((c, i) => (
                  <tr key={c.id || i}>
                    <td>
                      <button
                        className="text-primary border-0 bg-transparent p-0"
                        onClick={() => handleCaseDetails(c)}
                        disabled={loading}
                      >
                        {c.id || 'N/A'}
                      </button>
                    </td>
                    <td>
                      <button
                        className="text-primary border-0 bg-transparent p-0"
                        onClick={() => handleCaseDetails(c)}
                        disabled={loading}
                      >
                        {c.case_name || 'N/A'}
                      </button>
                    </td>
                    <td>{c.medical_center?.name || c.from_medical_center?.name || 'N/A'}</td>
                    <td>{c.disapproval_notes || 'N/A'}</td>
                    <td>{c.updated_at ? formatCaseDate(c.updated_at) : 'N/A'}</td>
                    <td>
                      <button
                        className="btn p-0 border-0"
                        onClick={() => handleCaseDetails(c)}
                        disabled={loading}
                        aria-label="View case details"
                      >
                        <i className="ti ti-list-details text-success fs-5"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredDisapprovedCases.length > 0 && (
          <Pagination
            total={filteredDisapprovedCases.length}
            limit={limit}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            disabled={loading}
          />
        )}
      </>
    );
  };

  // Filter panel
  const renderFilter = () => (
    <div className="filter-search mt-5">
      <h6 className="px-2 mb-0">Filter</h6>
      <div className="filter-panel">
        <form className="row">
          <div className="form-group col-md-6 col-lg-6 col-12 p-2">
            <input
              type="text"
              className="form-control"
              name="name"
              placeholder="Patient Name"
              value={filterForm.name}
              onChange={handleFilterChange}
            />
          </div>
          <div className="form-group col-lg-2 p-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-rounded"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear Filter
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render error state
  if (error) {
    return (
      <div className="alert alert-danger m-4">
        {error}
        <button
          className="btn btn-primary ms-3"
          onClick={() => {
            setError(null);
            setLoading(true);
            fetchDisapprovedCases();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Rejected Cases</h5>
                <p className="card-subtitle mb-0 mt-1">View and manage all Rejected patient transfer cases.</p>
                {roleName === 'EMS Team' && (
                  <button
                    type="button"
                    className="btn btn-primary btn-rounded mt-3"
                    onClick={openAddModal}
                    disabled={loading}
                  >
                    Add New Case
                  </button>
                )}
              </div>
              <div className="card-body p-4">
                {renderFilter()}
                {renderTable()}
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default RejectionTab;